<template>
  <div class="container" id="profile_section">

    <back-btn to="/dashboard" class="ml-3 profile-back-btn"></back-btn>

    <!-- New-user setup banner (non-dismissible) -->
    <div v-if="isNewUser" id="setup-banner"
      class="d-flex align-items-center mt-3 px-4 py-3 rounded text-white"
      style="background:linear-gradient(135deg,#d6b674 0%,#c4a55a 100%);box-shadow:0 3px 12px rgba(214,182,116,0.4)">
      <i class="fas fa-user-edit fa-lg flex-shrink-0 mr-3"></i>
      <div class="flex-grow-1" style="font-size:0.92rem;line-height:1.4">
        <strong>Complete your profile to get started.</strong>
        <span class="d-block">Set your name, username, Timezone and a password below, then click <em>Save Changes</em>.</span>
      </div>
      <button class="btn btn-sm btn-outline-light ml-3" @click="scrollToSecurity">
        Set Password <i class="fas fa-arrow-down ml-1"></i>
      </button>
    </div>

    <!-- Registration Progress Banner -->
    <div
      v-if="profile.status == 1"
      v-show="profile.reg_status != 'complete' && profile.reg_status != null && !isNewUser"
      class="regProgress mt-3"
    >
      <div class="reg-step completed">
        <div class="reg-step-circle"><i class="fas fa-check"></i></div>
        <div class="reg-step-label">Registered</div>
      </div>
      <div class="reg-step-line" :class="{ 'completed': profile.reg_status == 'tour_finished' || profile.reg_status == 'timezone_set' }"></div>
      <div class="reg-step" :class="{ 'completed': profile.reg_status == 'tour_finished' || profile.reg_status == 'timezone_set' }">
        <div class="reg-step-circle"><i class="fas fa-clock"></i></div>
        <div class="reg-step-label">Set Timezone</div>
      </div>
      <div class="reg-step-line" :class="{ 'completed': profile.reg_status == 'tour_finished' || profile.reg_status == 'timezone_set' }"></div>
      <div class="reg-step" :class="{ 'completed': profile.reg_status == 'tour_finished' || profile.reg_status == 'timezone_set' }">
        <div class="reg-step-circle"><i class="fas fa-flag-checkered"></i></div>
        <div class="reg-step-label">Complete</div>
      </div>
      <button v-if="profile.reg_status == 'tour_finished' || profile.reg_status == 'timezone_set'" @click="closeNotif" class="register-close">
        <i class="fas fa-times-circle"></i>
      </button>
    </div>

    <div v-if="this.$store.state.preFlightData !== null && this.$store.state.preFlightData.message" class="mt-3">
      <b-alert show variant="danger" class="text-center">
        <h3 v-text="this.$store.state.preFlightData.message"></h3>
      </b-alert>
    </div>


    <div v-if="profile.status == 1" class="profile-layout mt-3">

      <!-- ── Profile Hero Header ────────────────────────────── -->
      <div class="profile-hero">
        <div class="profile-hero-bg"></div>
        <div class="profile-hero-content">
          <div class="profile-avatar-wrap">
            <img
              class="profile-avatar"
              :src="$store.state.profile.profile_image || 'https://media.iamconnected.com/profile_images/default.png'"
              alt="Avatar"
            />
            <button class="avatar-edit-btn" @click="$bvModal.show('modal-profile-picture')" title="Change photo">
              <i class="fas fa-camera"></i>
            </button>
          </div>
          <div class="profile-hero-info">
            <h2 class="profile-hero-name">
              {{ profile.first_name }} {{ profile.last_name }}
              <span v-if="profile.email" class="profile-hero-username">
                <i class="fas fa-envelope mr-1"></i>{{ profile.email }}
              </span>
            </h2>
            <div class="profile-hero-meta">
              <span v-if="profile.city || profile.country" class="hero-meta-item">
                <i class="fas fa-map-marker-alt"></i>
                {{ [profile.city, profile.country].filter(Boolean).join(', ') }}
              </span>
              <span v-if="profile.is_coach" class="hero-coach-badge">
                <i class="fas fa-star mr-1"></i>Coach
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Section: Personal Info ─────────────────────────── -->
      <div class="profile-section-card">
        <div class="section-heading">
          <i class="fas fa-user section-heading-icon"></i>
          <span>Personal Information</span>
        </div>

        <div class="section-body">
          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">Full Name <span class="text-danger">*</span></label>
            <div class="col-lg-4 mb-2">
              <input type="text" v-model="profile.first_name" class="form-control profile-input"
                id="first_name" placeholder="First Name" @keyup="checkFormValid($event)" required
                :class="{ 'is-invalid': errors.first_name }" />
              <small v-if="errors.first_name" v-text="errors.first_name" class="invalid-feedback d-block"></small>
            </div>
            <div class="col-lg-4 mb-2">
              <input type="text" v-model="profile.last_name" class="form-control profile-input"
                id="last_name" placeholder="Family Name" @keyup="checkFormValid($event)" required
                :class="{ 'is-invalid': errors.last_name }" />
              <small v-if="errors.last_name" v-text="errors.last_name" class="invalid-feedback d-block"></small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">Screen Name <span class="text-danger">*</span></label>
            <div class="col-lg-4 mb-2">
              <input type="text" v-model="profile.user_name" class="form-control profile-input"
                id="user_name" placeholder="@username" @keyup="checkFormValid($event)" required
                :class="{ 'is-invalid': errors.user_name }" />
              <small v-if="errors.user_name" v-text="errors.user_name" class="invalid-feedback d-block"></small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">Email <span class="text-danger">*</span></label>
            <div class="col-lg-6 mb-2">
              <input type="email" v-model="profile.email" class="form-control profile-input"
                id="email" @keyup="checkFormValid($event)" required
                :class="{ 'is-invalid': errors.email }" />
              <small v-if="errors.email" v-text="errors.email" class="invalid-feedback d-block"></small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">Timezone <span class="text-danger">*</span></label>
            <div class="col-lg-9 mb-2">
              <timezone-select :profile="profile" @tzUpdated="handleTzUpdated($event)" />
              <small v-if="errors.timezoneOffsetH" v-text="errors.timezoneOffsetH" class="invalid-feedback d-block"></small>
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">Location</label>
            <div class="col-lg-3 mb-2">
              <input type="text" v-model="profile.city" class="form-control profile-input"
                id="city" placeholder="City" maxlength="100" @keyup="checkFormValid($event)" />
            </div>
            <div class="col-lg-3 mb-2">
              <input type="text" v-model="profile.country" class="form-control profile-input"
                id="country" placeholder="Country" maxlength="100" @keyup="checkFormValid($event)" />
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">
              Summary
              <span class="char-count">({{ (profile.summary || '').length }}/255)</span>
            </label>
            <div class="col-lg-6 mb-2">
              <input type="text" v-model="profile.summary" class="form-control profile-input"
                id="summary" maxlength="255" placeholder="A short summary about yourself"
                @keyup="checkFormValid($event)" />
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">
              WhatsApp
              <span class="text-muted small d-block font-weight-normal">(optional)</span>
            </label>
            <div class="col-lg-5 mb-2">
              <div class="social-input-wrap">
                <i class="fab fa-whatsapp social-icon" style="color: #25D366;"></i>
                <input
                  type="tel"
                  v-model="profile.whatsapp_number"
                  class="form-control profile-input"
                  id="whatsapp_number"
                  placeholder="+1 234 567 8900"
                  maxlength="20"
                  @keyup="checkFormValid($event)"
                  @input="validateWhatsappNumber"
                  :class="{ 'is-invalid': errors.whatsapp_number }"
                />
              </div>
              <small v-if="errors.whatsapp_number" class="text-danger d-block">
                <i class="fas fa-exclamation-circle mr-1"></i>{{ errors.whatsapp_number }}
              </small>
              <small v-else class="text-muted">
                <i class="fas fa-lock mr-1"></i>Not publicly visible. Include country code (e.g. +44).
              </small>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Section: Coach Details (coaches only) ──────────── -->
      <div v-if="profile.is_coach" class="profile-section-card">
        <div class="section-heading">
          <i class="fas fa-star section-heading-icon"></i>
          <span>Coach Details</span>
        </div>
        <div class="section-body">
          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">
              Expertise
              <span class="char-count">({{ (profile.expertise || '').length }}/255)</span>
            </label>
            <div class="col-lg-6 mb-2">
              <input class="form-control profile-input" v-model="profile.expertise"
                type="text" id="expertise" maxlength="255"
                placeholder="Your key areas of expertise" @keyup="checkFormValid($event)" />
            </div>
          </div>

          <div class="form-group row">
            <label class="col-lg-3 col-form-label field-label">Social Links</label>
            <div class="col-lg-6 mb-2">
              <div class="social-input-wrap">
                <i class="fab fa-linkedin social-icon"></i>
                <input class="form-control profile-input" v-model="profile.linkedin" type="text" placeholder="LinkedIn" />
              </div>
              <div class="social-input-wrap mt-2">
                <i class="fab fa-facebook social-icon"></i>
                <input class="form-control profile-input" v-model="profile.facebook" type="text" placeholder="Facebook" />
              </div>
              <div class="social-input-wrap mt-2">
                <i class="fab fa-instagram social-icon"></i>
                <input class="form-control profile-input" v-model="profile.instagram" type="text" placeholder="Instagram" />
              </div>
              <div class="social-input-wrap mt-2">
                <i class="fab fa-twitter social-icon"></i>
                <input class="form-control profile-input" v-model="profile.twitter" type="text" placeholder="Twitter" />
              </div>
              <div class="social-input-wrap mt-2">
                <i class="fab fa-tiktok social-icon"></i>
                <input class="form-control profile-input" v-model="profile.tiktok" type="text" placeholder="TikTok" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Section: Security ──────────────────────────────── -->
      <div class="profile-section-card">
        <div class="section-heading">
          <i class="fas fa-lock section-heading-icon"></i>
          <span>Security</span>
        </div>
        <div class="section-body">
          <div class="form-group row mb-2" id="security-section">
            <label class="col-lg-3 col-form-label field-label">
              {{ isNewUser ? 'Set Password' : 'New Password' }}
              <span v-if="isNewUser" class="text-danger">*</span>
            </label>
            <div class="col-lg-4 mb-2">
              <input type="password" v-model="password" class="form-control profile-input"
                id="password"
                :placeholder="isNewUser ? 'Create a password' : 'New password'"
                autocomplete="new-password"
                :required="isNewUser"
                :class="{ 'is-invalid': errors.password }"
                @change="checkFormValid($event)" />
              <small v-if="errors.password" v-text="errors.password" class="invalid-feedback d-block"></small>
            </div>
            <div class="col-lg-4 mb-2">
              <input type="password" v-model="password_confirm" class="form-control profile-input"
                id="password_confirm"
                placeholder="Confirm password"
                autocomplete="new-password"
                :required="isNewUser"
                @input="checkPassword" />
            </div>
          </div>
          <p v-if="!isNewUser" class="text-muted small ml-lg-3 pl-lg-3">
            <i class="fas fa-info-circle mr-1"></i>Leave blank to keep your current password.
          </p>
        </div>
      </div>

      <!-- ── Section: Availability ──────────────────────────── -->
      <div class="profile-section-card">
        <div class="section-heading">
          <i class="fas fa-user-clock section-heading-icon"></i>
          <span>Availability</span>
        </div>
        <div class="section-body">
          <PeerSupportSettings
            :schedule="profile.peer_support_schedule"
            @update:schedule="profile.peer_support_schedule = $event"
            @change="onPeerSupportChange"
          />
        </div>
      </div>

      <!-- ── Section: Notifications ─────────────────────────── -->
      <div class="profile-section-card">
        <div class="section-heading">
          <i class="fas fa-bell section-heading-icon"></i>
          <span>Notifications</span>
        </div>
        <div class="section-body">

          <div class="profile-notifications mb-1">
            <b-form-checkbox
              v-model="allow_whatsapp_contact"
              switch
              @change="onWhatsappContactToggle"
            >
              Allow WhatsApp contact.
            </b-form-checkbox>
            <small v-if="whatsapp_contact_warning" class="d-block mt-1" style="color: #d6b674;">
              <i class="fab fa-whatsapp mr-1"></i>Please add your WhatsApp number above before enabling this.
            </small>
          </div>

          <b-form-checkbox-group
            v-model="notifications_selected"
            :options="notifications_options"
            stacked
            switches
            @change="notificationUpdate"
            class="profile-notifications"
          ></b-form-checkbox-group>
        </div>
      </div>

      <!-- ── Save Button ─────────────────────────────────────── -->
      <div class="profile-save-bar">
        <div v-if="errors.profile != ''" class="save-feedback mb-2" :class="update_class">
          <small v-html="errors.profile"></small>
        </div>
        <button type="button" class="btn btn-save btn-secondary"
          @click="updateProfile">
          <i class="fas mr-2" :class="btn_update_icon"></i>Save Changes
        </button>
      </div>

    </div>

    <!-- Loading state -->
    <div v-else class="text-center gold m-5">
      <i class="fas fa-atom fa-spin fa-2x"></i>
    </div>

    <!-- ── Additional cards row ───────────────────────────────── -->
    <div class="row" v-if="profile.status == 1">
      <data-streams />

      <div class="col-12 mb-3">
        <div class="card">
          <h5 class="card-header section-card-header">
            <i class="fas fa-file-invoice-dollar mr-2"></i>Subscriptions
          </h5>
          <div class="card-body text-center">
            <a href="/payments/subscriptions" class="btn btn-outline-gold">
              <i class="far fa-credit-card mr-1"></i> Manage Subscriptions
            </a>
          </div>
        </div>
      </div>

      <div class="col-12 mb-3">
        <div class="card">
          <h5 class="card-header section-card-header">
            <i class="fas fa-download mr-2"></i>Download Your Data
          </h5>
          <div class="card-body text-center">
            <div class="d-flex justify-content-center" style="gap: 12px">
              <DownloadCSVButton
                v-if="profile.id"
                :userid="profile.id"
                class_style="btn btn-sm btn-outline-gold"
                text="Download Data"
              />
              <DownloadRemoteCSVButton text="Download Daily Log"></DownloadRemoteCSVButton>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile picture modal -->
    <b-modal
      id="modal-profile-picture"
      title="Update Profile Picture"
      ref="modal-profile-picture"
      ok-only
      ok-title="Save Photo"
      @ok="crop()"
      @close="reset()"
      no-close-on-backdrop
    >
      <b-form-file type="file" ref="file" accept="image/*" @change="loadImage($event)"></b-form-file>
      <hr v-if="image.src !== null">
      <cropper
        ref="cropper"
        class="upload-example-cropper"
        :src="image.src"
        :stencil-props="{ aspectRatio: 1 / 1 }"
        :canvas="{ height: 256, width: 256 }"
      />
    </b-modal>

  </div>
</template>

<script>
import axios from "@/axios";
import BackBtn from "../components/BackBtn.vue";
import DataStreams from "../components/DataStreams.vue";
import TimezoneSelect from "../components/TimezoneSelect.vue";
import DownloadCSVButton from '../components/DownloadCSVButton.vue';
import PeerSupportSettings from "../components/PeerSupportSettings.vue";

import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import DownloadRemoteCSVButton from "@/components/DownloadRemoteCSVButton";

export default {
  name: "profile",
  components: {
    BackBtn,
    DownloadRemoteCSVButton,
    Cropper,
    DataStreams,
    TimezoneSelect,
    DownloadCSVButton,
    PeerSupportSettings,
  },
  data() {
    return {
      downloadCsv: false,
      image: {
        src: null,
        type: null,
      },
      btn_update_class: "btn-secondary",
      btn_update_icon: "fa-pen",
      disableSubmit: false,
      errors: {
        email: "",
        first_name: "",
        last_name: "",
        user_name: "",
        timezoneOffsetH: "",
        password: "",
        profile: "",
        profile_image: '',
        whatsapp_number: "",
      },
      notifications_options: [
        // TODO: enable once challenge chat email sending is implemented
        // {
        //   text: "Email me when there is activity in challenge chat.",
        //   value: "challenge_chat",
        // },
        {
          text: "Email me if no contractions logged after 3 days.",
          value: "contractions_reminder",
        },
        {
          text: "Email me if no meditation logged after 3 days.",
          value: "meditation_reminder",
        },
        {
          text: "Email me when there is activity in the chat.",
          value: "chat_notification",
        },
      ],
      notifications_selected: [],
      allow_whatsapp_contact: false,
      whatsapp_contact_warning: false,
      password: "",
      password_confirm: "",
      update_class: "gold",
      finalBlob: null,
      base64data: null,
      originalSnapshot: null,
    };
  },
  methods: {
    getMimeType(file, fallback = null) {
      const byteArray = new Uint8Array(file).subarray(0, 4);
      let header = "";
      for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16);
      }
      switch (header) {
        case "89504e47":
          return "image/png";
        case "47494638":
          return "image/gif";
        case "ffd8ffe0":
        case "ffd8ffe1":
        case "ffd8ffe2":
        case "ffd8ffe3":
        case "ffd8ffe8":
          return "image/jpeg";
        default:
          return fallback;
      }
    },
    crop() {
      let _this = this;
      const { canvas } = this.$refs.cropper.getResult();

      canvas.toBlob((blob) => {
        // Do something with blob: upload to a server, download and etc.
        var reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = function() {
          _this.base64data = reader.result;
          _this.finalBlob = blob;
          _this.updateProfileImage();
        }
      }, this.image.type);
    },
    reset() {
      this.image = {
        src: null,
        type: null,
      };
    },
    loadImage(event) {
      const { files } = event.target;

      if (files && files[0]) {
        if (this.image.src) {
          URL.revokeObjectURL(this.image.src);
        }
        const blob = URL.createObjectURL(files[0]);
        const reader = new FileReader();

        reader.onload = (e) => {
          this.image = {
            src: blob,
            type: this.getMimeType(e.target.result, files[0].type),
          };
        };
        reader.readAsArrayBuffer(files[0]);
      }
    },
    destroyed() {
      // Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
      if (this.image.src) {
        URL.revokeObjectURL(this.image.src);
      }
    },

    checkPassword() {
      if (this.isNewUser && !this.password) {
        this.errors.password = "A password is required";
        return false;
      }
      if (this.password && this.password.length < 6) {
        this.errors.password = "The password must be at least 6 characters long";
        return false;
      }
      if (this.password_confirm && this.password !== this.password_confirm) {
        this.errors.password = "Passwords do not match!";
        return false;
      }
      if (!this.password_confirm && this.password) {
        this.errors.password = "";
        return true;
      }
      this.errors.password = "";
      return true;
    },
    scrollToSecurity() {
      const el = document.getElementById('security-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    checkEmail() {
      let _this = this;
      var is_valid = false;
      if (!this.validEmail(this.profile.email)) {
        this.errors.email = "A valid Email is required";
      } else {
        this.errors.email = "";
        is_valid = true;
      }

      if (is_valid == true) {
        // check if unique email
        let params = new URLSearchParams();
        params.append("email", _this.profile.email);

        axios({
          url: "emailCheck",
          params: params,
        })
          .then(function(response) {
            if (response.data === null) {
              _this.errors.email = "Email is already in use";
            } else {
              _this.errors.email = "";
            }
          })
          .catch(function() {});
      }
    },
    checkUsername() {
      let _this = this;
      let params = new URLSearchParams();
      params.append("user_name", _this.profile.user_name);

      axios({
        url: "userNameCheck",
        params: params,
      })
        .then(function(response) {
          if (response.data === null) {
            _this.errors.user_name = "Username is already in use";
          }
        })
        .catch(function() {});
    },
    checkFormValid(e) {
      console.log("checkFormValid");
      var required = e.target.required;
      var id = e.target.id;
      var val = e.target.value;
      const errors = Object.keys(this.errors);

      /* set errors */
      this.errors.profile = "";
      for (const key of errors) {
        if (key === id && required == true) {
          if (val === "") {
            this.errors[key] = key + " is required";
          } else this.errors[key] = "";
        }

        // @todo change it, gets called after keyup on any input
        if (key === "user_name") {
          this.checkUsername();
        } else if (key === "email") {
          this.checkEmail();
        } else if (key === "password" || key === "password_confirm") {
          this.checkPassword();
        }
      }

      var form_is_valid = true;
      for (const obj of Object.entries(this.errors)) {
        if (obj[1] !== "") {
          form_is_valid = false;
          break;
        }
      }

      if (form_is_valid == true) {
        this.disableSubmit = true;
        this.btn_update_class = "btn-success";
        this.btn_update_icon = "fa-hand-point-right";
      } else {
        this.disableSubmit = false;
        this.btn_update_class = "btn-secondary";
        this.btn_update_icon = "fa-pen";
      }

      return form_is_valid;
    },
    closeNotif() {
      let _this = this;

      let params = new URLSearchParams();
      params.append('reg_status', 'complete');

      axios({
        url: "regStatusUpdate",
        params: params,
      })
        .then(function(response) {
          if (response.data.status == 1) {
            _this.profile.reg_status = 'complete';
          } else {
            console.log("Response error");
          }
        })
        .catch(function() {});
    },
    notifigationsGet() {
      let _this = this;

      axios({
        url: "user_notifigation_settings_Get",
      })
        .then(function(response) {
          console.log(response.data.notifications);
          const notifKeys = ['chat_notification', 'message_received', 'challenge_chat', 'contractions_reminder', 'meditation_reminder', 'allow_whatsapp_contact'];
          notifKeys.forEach(key => {
            if (response.data.notifications[key] == 0) delete response.data.notifications[key];
          });
          console.log(response.data.notifications);
          _this.notifications_selected = Object.keys(
            response.data.notifications
          );
          _this.allow_whatsapp_contact = !!_this.profile.allow_whatsapp_contact;
          _this.$nextTick(() => _this.takeSnapshot());
        })
        .catch(function() {});
    },
    notificationUpdate() {},
    validateWhatsappNumber() {
      const val = (this.profile.whatsapp_number || '').trim();
      if (val === '') {
        this.errors.whatsapp_number = '';
        return true;
      }
      const cleaned = val.replace(/[\s\-().]/g, '');
      const valid = /^\+[1-9]\d{6,14}$/.test(cleaned);
      this.errors.whatsapp_number = valid ? '' : 'Enter a valid number with country code (e.g. +44 7700 900000).';
      if (valid) this.whatsapp_contact_warning = false;
      return valid;
    },
    onWhatsappContactToggle(checked) {
      if (checked && !this.profile.whatsapp_number) {
        this.allow_whatsapp_contact = false;
        this.whatsapp_contact_warning = true;
      } else {
        this.whatsapp_contact_warning = false;
      }
    },
    onPeerSupportChange() {},
    validateForm() {
      const rules = [
        { key: 'first_name', check: () => !!this.profile.first_name?.trim(), msg: 'First name is required' },
        { key: 'last_name', check: () => !!this.profile.last_name?.trim(), msg: 'Family name is required' },
        { key: 'user_name', check: () => !!this.profile.user_name?.trim(), msg: 'Username is required' },
        { key: 'email', check: () => this.validEmail(this.profile.email), msg: this.profile.email?.trim() ? 'A valid email address is required' : 'Email is required' },
        { key: 'timezoneOffsetH', check: () => this.profile.timezoneOffsetH != null && this.profile.timezoneOffsetH !== '', msg: 'Please set your timezone before saving' },
      ];

      let valid = true;
      rules.forEach(({ key, check, msg }) => {
        if (!check()) { this.errors[key] = msg; valid = false; }
        else this.errors[key] = '';
      });

      if (!this.checkPassword()) valid = false;
      return valid;
    },
    focusFirstError() {
      const firstInvalidInput = document.querySelector('#profile_section')?.querySelector('.is-invalid, .invalid-feedback')

      if (firstInvalidInput) {
        firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => firstInvalidInput.focus(), 200)
      }
    },
    updateProfile() {
      console.log('updateProfile...');
      const formValid = this.validateForm();
      const whatsappValid = this.validateWhatsappNumber();
      if (!formValid || !whatsappValid) {
        this.$nextTick(() => this.focusFirstError());
        return false;
      }
      let _this = this;
      this.update_class = 'gold';
      this.errors.profile = '<i class="fas fa-atom fa-spin fa-2x"></i>';
      const pss = _this.profile.peer_support_schedule;
      const data = {
        first_name: _this.profile.first_name,
        last_name: _this.profile.last_name,
        user_name: _this.profile.user_name,
        email: _this.profile.email,
        timezoneOffsetH: _this.profile.timezoneOffsetH,
        city: _this.profile.city || '',
        country: _this.profile.country || '',
        peer_support_schedule: pss ? (typeof pss === 'object' ? JSON.stringify(pss) : pss) : '',
        password: _this.password,
        notifications: _this.notifications_selected,
        summary: _this.profile.summary,
        expertise: _this.profile.expertise,
        linkedin: _this.profile.linkedin,
        facebook: _this.profile.facebook,
        instagram: _this.profile.instagram,
        twitter: _this.profile.twitter,
        tiktok: _this.profile.tiktok,
        whatsapp_number: _this.profile.whatsapp_number || '',
        allow_whatsapp_contact: _this.allow_whatsapp_contact ? 1 : 0,
      };

      axios({
        method: 'post',
        url: 'userUpdate',
        data,
      })
        .then(function(response) {
          _this.errors.profile = response.data.message;

          if (response.data.status == 1) {
            const wasNewUser = _this.isNewUser;
            _this.$store.commit("profileSet", response.data);
              if (wasNewUser) {
                _this.$router.push(`/programs/${_this.$route.query.pid || ''}`);
              }
          } else {
            _this.update_class = response.data.class;
          }
        })
        .catch(function(error) {
          const data = error.response?.data;
          _this.update_class = 'text-danger';
          _this.errors.profile = data?.message || 'Something went wrong. Please try again.';
        });
    },
    updateProfileImage() {
      let _this = this;

      let post = new FormData();

      if (_this.finalBlob != null) {
        post.append('profile_image', _this.finalBlob);
      }
      _this.destroyed();
      _this.reset();


      axios({
        method: "post",
        url: "userProfileImageUpdate",
        data: post,
        headers: {
          'Access-Control-Allow-Origin': '*',
          "Content-Type": "multipart/form-data"
        }
      })
        .then(function(response) {
          _this.errors.profile = response.data.message;

          if (response.data.status == 1) {
            _this.disableSubmit = false;
            _this.btn_update_class = "btn-secondary";
            _this.btn_update_icon = "fa-pen";

            //@todo don't commit whole profile here, just profile image may be
            _this.$store.commit("profileSet", response.data);
          } else {
            _this.update_class = response.data.class;
            console.log("Response error");
          }
        })
        .catch(function() {});
    },
    takeSnapshot() {
      const p = this.profile;
      this.originalSnapshot = {
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        user_name: p.user_name || '',
        email: p.email || '',
        timezoneOffsetH: p.timezoneOffsetH,
        city: p.city || '',
        country: p.country || '',
        summary: p.summary || '',
        expertise: p.expertise || '',
        linkedin: p.linkedin || '',
        facebook: p.facebook || '',
        instagram: p.instagram || '',
        twitter: p.twitter || '',
        tiktok: p.tiktok || '',
        whatsapp_number: p.whatsapp_number || '',
        allow_whatsapp_contact: this.allow_whatsapp_contact ? 1 : 0,
        peer_support_schedule: JSON.stringify(p.peer_support_schedule || null),
        notifications: JSON.stringify([...this.notifications_selected].sort()),
      };
    },
    validEmail(email) {
      var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      var val = re.test(email);
      console.log(email + ":" + val);
      return val;
    },
    handleTzUpdated(tz) {
      this.profile.timezoneOffsetH = tz;
      if (tz) {
        this.errors.timezoneOffsetH = '';
      }
    }
  },
  computed: {
    profile() {
      return this.$store.state.profile;
    },
    isNewUser() {
      return this.$store.state.profile.reg_status === 'pending_setup';
    },
    active_profile() {
      return this.$store.getters.profileGet;
    },
    user_level() {
      return this.active_profile.level;
    },
    hasChanges() {
      if (!this.originalSnapshot) return false;
      const p = this.profile;
      const o = this.originalSnapshot;
      return (
        (p.first_name || '') !== o.first_name ||
        (p.last_name || '') !== o.last_name ||
        (p.user_name || '') !== o.user_name ||
        (p.email || '') !== o.email ||
        p.timezoneOffsetH !== o.timezoneOffsetH ||
        (p.city || '') !== o.city ||
        (p.country || '') !== o.country ||
        (p.summary || '') !== o.summary ||
        (p.expertise || '') !== o.expertise ||
        (p.linkedin || '') !== o.linkedin ||
        (p.facebook || '') !== o.facebook ||
        (p.instagram || '') !== o.instagram ||
        (p.twitter || '') !== o.twitter ||
        (p.tiktok || '') !== o.tiktok ||
        (p.whatsapp_number || '') !== o.whatsapp_number ||
        (this.allow_whatsapp_contact ? 1 : 0) !== o.allow_whatsapp_contact ||
        JSON.stringify(p.peer_support_schedule || null) !== o.peer_support_schedule ||
        JSON.stringify([...this.notifications_selected].sort()) !== o.notifications ||
        this.password !== ''
      );
    },
  },
  created: function() {},
  mounted: function() {
    this.notifigationsGet();
    this.allow_whatsapp_contact = !!this.profile.allow_whatsapp_contact;
  },
  watch: {
    hasChanges(val) {
      this.disableSubmit = val;
      this.btn_update_class = val ? 'btn-success' : 'btn-secondary';
      this.btn_update_icon = val ? 'fa-hand-point-right' : 'fa-pen';
    },
    'profile.timezoneOffsetH'(val) {
      if ( val !== '') {
        this.errors.timezoneOffsetH = '';
      }
    },
  },
};
</script>

<style scoped>

.profile-back-btn {
  left: -8px;
  top: 8px;
}

/* ── Registration Progress ─────────────────────────────── */
.regProgress {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 12px;
  padding: 18px 24px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  position: relative;
  gap: 0;
}

.reg-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 0.4;
}

.reg-step.completed {
  opacity: 1;
}

.reg-step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  color: #aaa;
  transition: all 0.3s;
}

.reg-step.completed .reg-step-circle {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  box-shadow: 0 3px 10px rgba(214,182,116,0.4);
}

.reg-step-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.reg-step.completed .reg-step-label {
  color: #c4a55a;
}

.reg-step-line {
  flex: 1;
  height: 3px;
  background: #eee;
  margin: 0 8px;
  margin-bottom: 18px;
  border-radius: 2px;
  min-width: 40px;
  transition: background 0.3s;
}

.reg-step-line.completed {
  background: linear-gradient(90deg, #d6b674, #c4a55a);
}

.register-close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  color: #aaa;
  font-size: 1.1rem;
  cursor: pointer;
  outline: none;
}

.register-close:hover {
  color: #888;
}

/* ── Layout ────────────────────────────────────────────── */
.profile-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

/* ── Hero Header ───────────────────────────────────────── */
.profile-hero {
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(0,0,0,0.09);
}

.profile-hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #d6b674 0%, #b8913a 60%, #8a6b28 100%);
  opacity: 0.92;
}

.profile-hero-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 28px 32px;
}

.profile-avatar-wrap {
  position: relative;
  flex-shrink: 0;
}

.profile-avatar {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255,255,255,0.7);
  box-shadow: 0 4px 14px rgba(0,0,0,0.2);
}

.avatar-edit-btn {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #fff;
  border: none;
  color: #c4a55a;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transition: all 0.2s;
}

.avatar-edit-btn:hover {
  background: #c4a55a;
  color: #fff;
  transform: scale(1.1);
}

.profile-hero-info {
  flex: 1;
  min-width: 0;
}

.profile-hero-name {
  color: #fff;
  font-size: 1.55rem;
  font-weight: 700;
  margin: 0 0 6px;
  line-height: 1.2;
  text-shadow: 0 1px 4px rgba(0,0,0,0.15);
}

.profile-hero-username {
  display: block;
  font-size: 0.9rem;
  font-weight: 400;
  opacity: 0.8;
  margin-top: 2px;
}

.profile-hero-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hero-meta-item {
  color: rgba(255,255,255,0.85);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 5px;
}

.hero-coach-badge {
  background: rgba(255,255,255,0.2);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.4);
  border-radius: 20px;
  padding: 3px 12px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.4px;
}

/* ── Section Cards ─────────────────────────────────────── */
.profile-section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  overflow: hidden;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  background: linear-gradient(90deg, rgba(214,182,116,0.12) 0%, rgba(214,182,116,0.03) 100%);
  border-bottom: 1px solid rgba(214,182,116,0.2);
  border-left: 4px solid #d6b674;
  font-weight: 600;
  font-size: 0.92rem;
  color: #444;
  letter-spacing: 0.2px;
}

.section-heading-icon {
  color: #d6b674;
  font-size: 0.95rem;
  width: 18px;
  text-align: center;
}

.section-body {
  padding: 22px 24px 16px;
}

/* ── Form Fields ───────────────────────────────────────── */
.field-label {
  color: #555;
  font-size: 0.88rem;
  font-weight: 500;
}

.char-count {
  display: block;
  font-size: 0.72rem;
  color: #aaa;
  font-weight: 400;
}

.profile-input {
  border-color: #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.profile-input:focus {
  border-color: #d6b674;
  box-shadow: 0 0 0 3px rgba(214,182,116,0.15);
  outline: none;
}

/* ── Social Inputs ─────────────────────────────────────── */
.social-input-wrap {
  position: relative;
}

.social-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #d6b674;
  font-size: 0.85rem;
  z-index: 1;
  pointer-events: none;
}

.social-input-wrap .profile-input {
  padding-left: 34px;
}

/* ── Notifications ─────────────────────────────────────── */
.profile-notifications ::v-deep .custom-control-label {
  font-size: 0.88rem;
  color: #555;
  padding-top: 2px;
}

.profile-notifications ::v-deep .custom-switch .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #d6b674;
  border-color: #d6b674;
}

/* ── Save Bar ──────────────────────────────────────────── */
.profile-save-bar {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  padding: 20px 24px;
  text-align: center;
}

.save-feedback {
  margin-bottom: 8px;
}

.btn-save {
  min-width: 180px;
  padding: 10px 32px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.3px;
  transition: all 0.2s;
}

.btn-save.btn-success {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  border: 2px solid #c4a55a;
  color: #fff;
  box-shadow: 0 4px 14px rgba(214,182,116,0.4);
}

.btn-save.btn-success:hover:not(:disabled) {
  box-shadow: 0 6px 18px rgba(214,182,116,0.55);
  transform: translateY(-1px);
}

.btn-save.btn-secondary {
  background: #f0f0f0;
  border: 2px solid #ddd;
  color: #aaa;
}

/* ── Additional section card headers ───────────────────── */
.section-card-header {
  background: linear-gradient(90deg, rgba(214,182,116,0.12) 0%, rgba(214,182,116,0.03) 100%);
  border-left: 4px solid #d6b674;
  color: #444;
  font-weight: 600;
  font-size: 1rem;
}

.btn-outline-gold {
  color: #d6b674;
  border: 1px solid #d6b674;
  background: transparent;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-outline-gold:hover {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  border-color: transparent;
}

.gold {
  color: #d6b674;
}

/* ── Responsive ────────────────────────────────────────── */
@media (max-width: 800px) {
  .profile-hero-content {
    padding: 20px 18px;
  }

  .profile-avatar {
    width: 72px;
    height: 72px;
  }

  .profile-hero-name {
    font-size: 1.2rem;
  }

  .section-body {
    padding: 16px 16px 12px;
  }

  .section-heading {
    padding: 12px 16px;
    font-size: 0.82rem;
  }

  .section-card-header {
    font-size: 0.82rem;
  }

  .regProgress {
    padding: 14px 16px;
  }

  .reg-step-line {
    min-width: 20px;
  }
}

@media (max-width: 350px) {
  #profile_section.container {
    padding-left: 8px;
    padding-right: 8px;
  }

  .profile-hero-content {
    padding: 14px 12px;
    gap: 12px;
  }

  .profile-avatar {
    width: 52px;
    height: 52px;
  }

  .avatar-edit-btn {
    width: 22px;
    height: 22px;
    font-size: 0.6rem;
  }

  .profile-hero-name {
    font-size: 1rem;
  }

  .profile-hero-username {
    font-size: 0.75rem;
  }

  .hero-meta-item {
    font-size: 0.75rem;
  }

  .section-heading {
    padding: 10px 12px;
    font-size: 0.78rem;
  }

  .section-body {
    padding: 12px 10px 10px;
  }

  .field-label {
    font-size: 0.8rem;
  }

  .profile-input {
    font-size: 0.82rem;
  }

  .regProgress {
    padding: 10px 10px;
    gap: 0;
  }

  .reg-step-circle {
    width: 30px;
    height: 30px;
    font-size: 0.7rem;
  }

  .reg-step-label {
    font-size: 0.6rem;
  }

  .reg-step-line {
    min-width: 10px;
  }

  .btn-save {
    width: 100%;
    min-width: unset;
    padding: 10px 16px;
    font-size: 0.88rem;
  }

  .profile-save-bar {
    padding: 14px 10px;
  }
}
</style>
